import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/** Reusable shell for non-home page heroes. */
export function PageHero({ eyebrow, title, description, image, testid = "page-hero" }) {
  return (
    <section data-testid={testid} className="relative overflow-hidden border-b border-white/10">
      {image && (
        <div className="absolute inset-0">
          <img src={image} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/70 via-[#09090b]/80 to-[#09090b]" />
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-5 font-semibold">
          {eyebrow}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.95] max-w-4xl">
          {title}
        </h1>
        {description && (
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            {description}
          </p>
        )}
      </div>
    </section>
  );
}

export function Section({ children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`py-16 md:py-24 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">{children}</div>
    </section>
  );
}

export function SectionTitle({ eyebrow, title, description, align = "left" }) {
  return (
    <div className={`mb-10 md:mb-14 max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && (
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 font-semibold">
          {eyebrow}
        </div>
      )}
      <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1]">
        {title}
      </h2>
      {description && (
        <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed">{description}</p>
      )}
    </div>
  );
}

export function BulletGrid({ items, testid }) {
  return (
    <ul
      data-testid={testid}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10"
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="bg-[#09090b] p-5 md:p-6 flex items-start gap-3 hover:bg-[#18181b] transition-colors"
        >
          <span className="font-mono text-[10px] text-[#FFCC00] mt-1 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="text-sm md:text-[15px] text-white/85 leading-snug">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function FeatureCard({ index, title, description, testid }) {
  return (
    <div
      data-testid={testid}
      className="group relative bg-[#18181b] border border-white/10 p-7 md:p-8 hover:border-[#FFCC00]/60 hover:-translate-y-1 transition-all duration-300"
    >
      <div className="font-mono text-xs text-[#FFCC00] mb-4">
        {String(index).padStart(2, "0")} /
      </div>
      <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight mb-3">
        {title}
      </h3>
      <p className="text-sm text-white/70 leading-relaxed">{description}</p>
    </div>
  );
}

export function CTABand({
  title = "Ready to talk training?",
  description = "Reach out to learn how CLB Solution can support your drivers, your trainees, or your district.",
}) {
  return (
    <section className="relative border-y border-white/10 bg-[#0d0d10]" data-testid="cta-band">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid md:grid-cols-3 gap-8 items-center">
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 font-semibold">
            Contact CLB Solution
          </div>
          <h3 className="font-display text-2xl md:text-4xl font-bold uppercase leading-tight">
            {title}
          </h3>
          <p className="mt-3 text-white/65 max-w-xl">{description}</p>
        </div>
        <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
          <Link
            to="/contact"
            data-testid="cta-band-contact"
            className="inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-[#E6B800] transition-colors"
          >
            Request Information <ArrowUpRight size={16} />
          </Link>
          <a
            href="mailto:clbsolutionllc@gmail.com"
            data-testid="cta-band-email"
            className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:border-white transition-colors"
          >
            Email Us
          </a>
        </div>
      </div>
    </section>
  );
}
